import React, { useState } from "react";
import axios from "axios";
import { toast as message } from "react-toastify";
import NumberSearchPanel from "./NumberSearchPanel";

const TWILLIO_BASE_URL = process.env.REACT_APP_TWILLIO_BASE_URL;
const BASE_URL = process.env.REACT_APP_BASE_URL;

// Lets a user pick + claim their included free fax number right after subscribing
// to Starter (or later from the Subscription page). Reuses the Buy Numbers search
// UI (NumberSearchPanel). The first FaxNumber subscription on Starter is billed at
// $0 (price_free) by the API.
const ClaimFreeNumberModal = ({
  show,
  onClose,
  customerId,
  paymentMethodId,
  userId,
  onClaimed,
}) => {
  const [country, setCountry] = useState("US");
  const [areaCode, setAreaCode] = useState("");
  const [autoSuggest, setAutoSuggest] = useState(false);
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [claiming, setClaiming] = useState(false);

  if (!show) return null;

  const authHeader = { headers: { Authorization: localStorage.getItem("authToken") } };

  const handleSearch = async () => {
    setSearching(true);
    setResults(null);
    setSelected(null);
    try {
      const res = await axios.post(`${TWILLIO_BASE_URL}/numbers/search_number`, {
        country_code: country,
        area_code: autoSuggest ? "" : areaCode,
      });
      if (res.data?.status === "ERR") {
        message.error(res.data.ErrorMessage || "No numbers found.");
        setResults([]);
      } else {
        setResults(Array.isArray(res.data) ? res.data : []);
      }
    } catch {
      message.error("Failed to fetch numbers. Please try again.");
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const toggleNumber = (num) => {
    // single-select
    setSelected((prev) => (prev?.phone_number === num.phone_number ? null : num));
  };

  const handleClaim = async () => {
    if (!selected) {
      message.error("Please select a number to claim.");
      return;
    }
    if (!customerId) {
      message.error("Missing customer details. Please reload and try again.");
      return;
    }
    setClaiming(true);
    try {
      // Resolve a payment method: use the one from checkout, else the customer's default card
      let pmId = paymentMethodId;
      if (!pmId) {
        const pmRes = await axios.get(
          `${BASE_URL}/subscription/customers/${customerId}/payment-methods`,
          authHeader
        );
        const pms = pmRes.data?.result?.data?.paymentMethods || [];
        pmId = (pms.find((p) => p.is_default) || pms[0])?.id;
        if (!pmId) {
          throw new Error("No payment method on file. Please add a card first.");
        }
      }

      // 1. Provision the number (twilio)
      const buyRes = await axios.post(
        `${TWILLIO_BASE_URL}/numbers/buy_number`,
        {
          number: [selected.phone_number],
          number_country_code: 1,
          number_city_code: "251",
          number_type: "FAX",
          number_status: "enabled",
          provider_Name: "TWILIO",
          user_ID: userId,
          currency: "USD",
        },
        authHeader
      );

      const purchased = buyRes.data?.successfulPurchases ?? [];
      if (purchased.length === 0) {
        throw new Error(buyRes.data?.errors?.[0]?.Error ?? "Failed to provision the number.");
      }
      const faxNumberIds = purchased.map((p) => p.data?.number_ID).filter(Boolean);

      // 2. Create the (free) FaxNumber subscription
      const subRes = await axios.post(
        `${BASE_URL}/subscription/create-fax-subscriptions`,
        { customerId, paymentMethodId: pmId, faxNumberIds },
        authHeader
      );
      const subResults = subRes.data?.result?.data?.results ?? [];
      if (subResults.length === 0) throw new Error("Failed to activate the free number.");

      message.success("Your free fax number is ready!");
      onClaimed?.(selected.phone_number);
      onClose?.();
    } catch (err) {
      message.error(err.message || "Could not claim the free number. Please try again.");
    } finally {
      setClaiming(false);
    }
  };

  return (
    <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }} tabIndex="-1" role="dialog">
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="fas fa-gift me-2 text-success"></i>
              Claim your free fax number
            </h5>
            <button type="button" className="btn-close" onClick={onClose} disabled={claiming} aria-label="Close" />
          </div>
          <div className="modal-body">
            <p className="text-muted small mb-3">
              Your Starter plan includes <strong>one free fax number</strong>. Search and pick the
              one you'd like — you won't be charged for it.
            </p>

            <NumberSearchPanel
              country={country}
              onCountryChange={setCountry}
              areaCode={areaCode}
              onAreaCodeChange={setAreaCode}
              autoSuggest={autoSuggest}
              onAutoSuggestChange={setAutoSuggest}
              loading={searching}
              results={results}
              isSelected={(num) => selected?.phone_number === num.phone_number}
              onToggle={toggleNumber}
              onSearch={handleSearch}
              renderBadge={() => <span className="badge bg-success">FREE</span>}
            />
          </div>
          <div className="modal-footer">
            <button className="btn btn-light" onClick={onClose} disabled={claiming}>
              Skip for now
            </button>
            <button className="btn btn-success" onClick={handleClaim} disabled={claiming || !selected}>
              {claiming ? (
                <><span className="spinner-border spinner-border-sm me-1" />Claiming…</>
              ) : (
                <><i className="fas fa-gift me-1"></i>Claim free number</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClaimFreeNumberModal;
